import { useSelector } from 'react-redux';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomNotification, dialogFunction } from 'common/components';

import selectorsGridView from 'common/components/grid-view/controllers/selectors';

import * as creditCardServices from 'services/creditCardEula';
import * as productServices from 'services/product';
import {
  useRefetchAllMemberBillingStatement,
  useQueryMemberDiscountProperties,
  useQueryBillingNote,
} from 'pages/company-profile/components/tabs/billing/queries';
import { useGetMemberId } from 'hooks';

export const captureProductsQueryKey = (productIds) => [
  'credit-card',
  'products-for-capture',
  ...productIds,
];

export const mappingProductList = (productList) => {
  return productList?.map((product) => {
    return {
      ...product,
      initFee: product.fee,
    };
  });
};

export const useGetProductsForCapture = ({
  productIds,
  ...otherProps
} = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: captureProductsQueryKey(productIds),
    queryFn: async () => {
      return await creditCardServices.getProductsForCapture({ productIds });
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(productIds?.length > 0),
    staleTime: 0,
    ...otherProps,
  });

  const handleRefetchProductsForCapture = (productIds) => {
    queryClient.invalidateQueries({
      queryKey: captureProductsQueryKey(productIds),
    });
  };

  return {
    productList: mappingProductList(query?.data?.data?.products) ?? [],
    handleRefetchProductsForCapture,
    ...query,
  };
};

export const useMutationCreateProduct = () => {
  return useMutation({
    mutationFn: productServices.addProduct,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        CustomNotification.success('Create product successfully');
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong');
      }
    },
    onError: () => {
      CustomNotification.error('Failed to create new product');
    },
  });
};

export const mutationSendIXOneCaptureKeys = ['payment', 'send-ixone-capture'];

export const useMutationSendProductToCapture = (props = {}) => {
  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();

  const { memberId } = useGetMemberId();

  const { invalidateMemberDiscountProperties } =
    useQueryMemberDiscountProperties({ memberId, enabled: false });
  const { invalidateBillingNote } = useQueryBillingNote({
    memberId,
    enabled: false,
  });

  return useMutation({
    mutationKey: mutationSendIXOneCaptureKeys,
    mutationFn: creditCardServices.sendProductToCapture,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        CustomNotification.success('Send product(s) to capture successfully');
      } else {
        const errorMsg = response?.message ?? 'Something went wrong';
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
        });
      }
      refetchAllMemberBillingStatement();
      invalidateMemberDiscountProperties();
      invalidateBillingNote();
    },
    onError: () => {
      CustomNotification.error('Failed to send product(s) to capture');
    },
    ...props,
  });
};

export const useGetSelectedProductIdsGrid = () => {
  const selectProductGrid = useSelector(
    selectorsGridView.makeSelectDetailCurrentITemsSelection()
  );

  const selectedProductIdsGrid =
    selectProductGrid?.map((product) => parseInt(product?.id)) ?? [];

  return selectedProductIdsGrid;
};
